import React, { useState, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2, Edit2, GripVertical, ArrowRight, Check } from 'lucide-react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import MiniWaves from '@/components/animations/MiniWaves'
import { ProjectContext } from '../../../context/ProjectContext'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import { useChatStore } from '@/components/askAnything/chatStore'
import { nanoid } from '@/lib/nanoid'
import { AuthContext } from '@/context/AuthContext'
import toast from 'react-hot-toast'

interface Interviewee {
  id: string
  name: string
  company: string
  email: string
  phone: string
  privateComment: string
  interviewed: boolean
  position: number
}

interface EditingItem {
  type: string | null
  index: number | null
  value: Partial<Interviewee>
  section: string | null
}

interface Project {
  name: string
  findOutItems?: string[]
  product: string
  id: string
}

export function PeopleToInterview() {
  const { addToNewChatThread } = useChatStore()
  const navigate = useNavigate()
  const { project } = useContext(ProjectContext)
  const { user } = useContext(AuthContext)
  const [interviewees, setInterviewees] = useState<Interviewee[]>([])
  const [newInterviewee, setNewInterviewee] = useState<Partial<Interviewee>>({
    name: '',
    company: '',
    email: '',
    phone: '',
    privateComment: '',
    interviewed: false,
  })
  const [editingItem, setEditingItem] = useState<EditingItem>({
    type: null,
    index: null,
    value: {},
    section: null,
  })
  const [showForm, setShowForm] = useState(false)
  const customFetch = useCustomFetch()

  useEffect(() => {
    const fetchInterviewees = async () => {
      try {
        const response = await customFetch(`/interview-candidates/${project.id}`, 'GET')
        if (response.candidates) {
          setInterviewees(response.candidates)
        }
      } catch (error) {
        console.error('Error fetching interview candidates:', error)
      }
    }

    if (project?.id) {
      fetchInterviewees()
    }
  }, [project?.id])

  const saveInterviewees = async (updatedInterviewees: Interviewee[]) => {
    try {
      const response = await customFetch(`/interview-candidates/${project.id}`, 'PUT', {
        candidates: updatedInterviewees.map((candidate, index) => ({
          ...candidate,
          projectId: project.id,
          productId: project.product,
          position: index,
          created_at: Date.now(),
          created_by: user.id,
        })),
      })
      if (response.candidates) {
        setInterviewees(response.candidates)
      }
    } catch (error) {
      console.error('Error saving interview candidates:', error)
    }
  }

  const handleAddInterviewee = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newInterviewee.name?.trim()) {
      const intervieweeToAdd = {
        id: nanoid(),
        name: newInterviewee.name.trim(),
        company: newInterviewee.company?.trim() || '',
        email: newInterviewee.email?.trim() || '',
        phone: newInterviewee.phone?.trim() || '',
        privateComment: newInterviewee.privateComment?.trim() || '',
        interviewed: newInterviewee.interviewed || false,
        position: interviewees.length,
      }
      const updatedInterviewees = [...interviewees, intervieweeToAdd]
      await saveInterviewees(updatedInterviewees)
      setNewInterviewee({
        name: '',
        company: '',
        email: '',
        phone: '',
        privateComment: '',
        interviewed: false,
      })
      setShowForm(false)
    }
  }

  const handleDeleteItem = async (index: number) => {
    const updatedInterviewees = interviewees.filter((_, i) => i !== index)
    await saveInterviewees(updatedInterviewees)
  }

  const handleEditItem = (index: number, value: Interviewee) => {
    setEditingItem({ type: 'interviewee', index, value, section: null })
  }

  const handleSaveEdit = async (index: number) => {
    if (editingItem.value.name?.trim()) {
      const updatedInterviewees = [...interviewees]
      updatedInterviewees[index] = {
        ...updatedInterviewees[index],
        ...editingItem.value,
        name: editingItem.value.name!.trim(),
        company: editingItem.value.company?.trim() || updatedInterviewees[index].company,
        email: editingItem.value.email?.trim() || updatedInterviewees[index].email,
        phone: editingItem.value.phone?.trim() || updatedInterviewees[index].phone,
        privateComment: editingItem.value.privateComment?.trim() || updatedInterviewees[index].privateComment,
      }
      await saveInterviewees(updatedInterviewees)
    }
    setEditingItem({ type: null, index: null, value: {}, section: null })
  }

  const handleToggleInterviewed = async (index: number) => {
    const updatedInterviewees = [...interviewees]
    updatedInterviewees[index] = {
      ...updatedInterviewees[index],
      interviewed: !updatedInterviewees[index].interviewed,
    }
    await saveInterviewees(updatedInterviewees)
  }

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return

    const { source, destination } = result
    const items = Array.from(interviewees)
    const [reorderedItem] = items.splice(source.index, 1)
    items.splice(destination.index, 0, reorderedItem)
    await saveInterviewees(items)
  }

  const handleSuggestPersonas = async () => {
    // Validate productId (required)
    const productId = project.product
    if (!productId) {
      toast.error('Missing product. Please select a product for this project first.')
      return
    }
    // Format the interviewees as bullet points
    const potentialInterviewees = interviewees.map((item) => `• ${item.name} from ${item.company}`).join('\n')

    // Get the Things to Find Out items from the parent component
    const thingsToFindOut = project.findOutItems?.map((item: string) => `• ${item}`).join('\n') || ''

    // Create the query with the new format
    const query = `We're trying to find out:
${thingsToFindOut}

Been thinking of the following folks:
${potentialInterviewees}

Based on best user research practices and human-centered design, can you suggest any other user types or personas?
Are there any gaps in our project plan?`

    // Save current path for returning from Ask Anything
    localStorage.setItem('previousPath', window.location.pathname)

    // Navigate to Chats tab to keep navigation visible
    navigate('/dashboard/chats')

    await addToNewChatThread({
      productId,
      name: 'Interview Personas: ' + project.name,
      message: query,
    })
  }

  const renderItem = (item: Interviewee, index: number) => (
    <Draggable key={item.id} draggableId={item.id} index={index}>
      {(provided) => (
        <li
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="group bg-white rounded-lg border border-[#e4e4e4] p-3 flex items-start gap-2"
        >
          <div {...provided.dragHandleProps} className="mt-1">
            <GripVertical size={16} className="text-[#62708d]" />
          </div>

          {editingItem.type === 'interviewee' && editingItem.index === index ? (
            <div className="flex-1">
              <input
                type="text"
                value={editingItem.value.name || ''}
                onChange={(e) =>
                  setEditingItem((prev) => ({ ...prev, value: { ...prev.value, name: e.target.value } }))
                }
                placeholder="Name"
                className="w-full p-2 mb-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
              />
              <input
                type="text"
                value={editingItem.value.company || ''}
                onChange={(e) =>
                  setEditingItem((prev) => ({ ...prev, value: { ...prev.value, company: e.target.value } }))
                }
                placeholder="Company"
                className="w-full p-2 mb-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
              />
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={editingItem.value.email || ''}
                  onChange={(e) =>
                    setEditingItem((prev) => ({ ...prev, value: { ...prev.value, email: e.target.value } }))
                  }
                  placeholder="Email"
                  className="flex-1 p-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                />
                <input
                  type="text"
                  value={editingItem.value.phone || ''}
                  onChange={(e) =>
                    setEditingItem((prev) => ({ ...prev, value: { ...prev.value, phone: e.target.value } }))
                  }
                  placeholder="Phone"
                  className="flex-1 p-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                />
              </div>
              <textarea
                value={editingItem.value.privateComment || ''}
                onChange={(e) =>
                  setEditingItem((prev) => ({ ...prev, value: { ...prev.value, privateComment: e.target.value } }))
                }
                placeholder="Private Comment"
                className="w-full p-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                rows={2}
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => handleSaveEdit(index)}
                  className="text-[#FF4A8E] text-sm font-medium hover:underline"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingItem({ type: null, index: null, value: {}, section: null })}
                  className="text-[#3f424a] text-sm font-medium hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-[#3f424a]">{item.name}</div>
                  {item.company && <div className="text-sm text-[#62708d]">{item.company}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleInterviewed(index)}
                    className={`p-1 rounded ${item.interviewed ? 'bg-green-100' : 'bg-gray-100'}`}
                  >
                    <Check size={16} className={item.interviewed ? 'text-green-600' : 'text-gray-400'} />
                  </button>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleEditItem(index, item)} className="p-1 hover:bg-grey-100 rounded">
                      <Edit2 size={16} className="text-[#62708d]" />
                    </button>
                    <button onClick={() => handleDeleteItem(index)} className="p-1 hover:bg-grey-100 rounded">
                      <Trash2 size={16} className="text-[#62708d]" />
                    </button>
                  </div>
                </div>
              </div>
              {(item.email || item.phone) && (
                <div className="flex gap-4 mt-1 text-sm text-[#62708d]">
                  {item.email && <div>{item.email}</div>}
                  {item.phone && <div>{item.phone}</div>}
                </div>
              )}
              {item.privateComment && (
                <div className="mt-2 text-sm text-[#62708d] bg-gray-50 p-2 rounded">{item.privateComment}</div>
              )}
            </div>
          )}
        </li>
      )}
    </Draggable>
  )

  return (
    <div className="bg-white rounded-2xl p-6">
      <div className="py-[8px] border-b-2 border-grey-200 flex justify-between items-center">
        <p className="text-[#3f424a] text-4xl font-bold font-['DM_Sans'] leading-[41.40px]">👉 People to Interview</p>
        <div className="flex items-center justify-center cursor-pointer group">
          <button
            onClick={handleSuggestPersonas}
            className="relative flex items-center justify-between bg-gradient-to-br from-gray-900 to-black/80 px-4 py-2 rounded-full shadow-2xl hover:shadow-xl transition-all 
            border border-white/10 
            ring-2 ring-black/20 
            hover:ring-black/30 
            hover:scale-105 
            active:scale-95 
            transform 
            duration-200 
            ease-in-out
            animate-breathing"
          >
            <div className="flex items-center gap-2">
              <MiniWaves size="h-6 w-6" />
              <span className="font-bold text-white text-sm">Suggest Personas</span>
            </div>
          </button>
        </div>
      </div>

      <div className="mt-[36px]">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div>
            <Droppable droppableId="interviewees">
              {(provided) => (
                <ul ref={provided.innerRef} {...provided.droppableProps} className="space-y-3 mb-4">
                  {interviewees.map((item, index) => renderItem(item, index))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>

            {showForm ? (
              <form onSubmit={handleAddInterviewee} className="bg-white rounded-lg border border-[#e4e4e4] p-3">
                <input
                  type="text"
                  value={newInterviewee.name}
                  onChange={(e) => setNewInterviewee((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Name"
                  className="w-full p-2 mb-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                />
                <input
                  type="text"
                  value={newInterviewee.company}
                  onChange={(e) => setNewInterviewee((prev) => ({ ...prev, company: e.target.value }))}
                  placeholder="Company"
                  className="w-full p-2 mb-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                />
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newInterviewee.email}
                    onChange={(e) => setNewInterviewee((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="Email"
                    className="flex-1 p-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={newInterviewee.phone}
                    onChange={(e) => setNewInterviewee((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone"
                    className="flex-1 p-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                  />
                </div>
                <textarea
                  value={newInterviewee.privateComment}
                  onChange={(e) => setNewInterviewee((prev) => ({ ...prev, privateComment: e.target.value }))}
                  placeholder="Private Comment"
                  className="w-full p-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                  rows={2}
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button type="submit" className="text-[#FF4A8E] text-sm font-medium hover:underline">
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false)
                      setNewInterviewee({
                        name: '',
                        company: '',
                        email: '',
                        phone: '',
                        privateComment: '',
                        interviewed: false,
                      })
                    }}
                    className="text-[#3f424a] text-sm font-medium hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowForm(true)}
                  className="flex-1 p-2 border border-[#e4e4e4] rounded-lg text-left text-gray-500 hover:border-[#FF4A8E] transition-colors"
                >
                  Add a person to interview...
                </button>
                <button
                  onClick={() => setShowForm(true)}
                  className="p-2 bg-[#FF4A8E] text-white rounded-lg hover:bg-[#ff3a7e] transition-colors"
                >
                  <ArrowRight size={20} />
                </button>
              </div>
            )}
          </div>
        </DragDropContext>
      </div>
    </div>
  )
}
